import { Card, Grid, Skeleton, Stack, Text, Title } from '@mantine/core';
import type { GroupSummary } from '../utils/computeGroupSummary';
import { formatStatValue } from '../utils/formatStatValue';

type OverviewKpiGridProps = {
  summary: GroupSummary;
  isLoading?: boolean;
};

function KpiValueSkeleton() {
  return (
    <Stack gap={8} mt={4}>
      <Skeleton height={28} width='55%' radius='sm' />
      <Skeleton height={12} width='85%' radius='sm' />
      <Skeleton height={12} width='70%' radius='sm' />
    </Stack>
  );
}

export function OverviewKpiGrid({
  summary,
  isLoading = false,
}: OverviewKpiGridProps) {
  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder h='100%'>
          <Title order={5}>Sessioni perfette</Title>
          {isLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <Text size='xl' fw={700}>
                {summary.perfectSessions}/{summary.readySessions}
              </Text>
              <Text c='dimmed' size='sm'>
                {summary.sessionsWithEventIssues} con gap o duplicati
                (post-analisi)
              </Text>
              <Text c='dimmed' size='sm' mt='xs'>
                Sessioni senza problemi rilevati nei record eventi, utili per
                una lettura più lineare del campione.
              </Text>
            </>
          )}
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder h='100%'>
          <Title order={5}>Media HR gruppo</Title>
          {isLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <Text size='xl' fw={700}>
                {formatStatValue(summary.meanHr)} ±{' '}
                {formatStatValue(summary.meanHrStd)} bpm
              </Text>
              <Text c='dimmed' size='sm' mt='xs'>
                Media della frequenza cardiaca sul tratto completo per sessione,
                aggregata sul gruppo. Il simbolo ± indica quanto i valori
                variano tra i partecipanti.
              </Text>
            </>
          )}
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder h='100%'>
          <Title order={5}>Media LF/HF gruppo</Title>
          {isLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <Text size='xl' fw={700}>
                {formatStatValue(summary.meanLfHfRatio)} ±{' '}
                {formatStatValue(summary.meanLfHfRatioStd)}
              </Text>
              <Text c='dimmed' size='sm' mt='xs'>
                Media del rapporto LF/HF (PSD Welch su tachogramma NN) sul
                tratto completo per sessione, aggregata sul gruppo.
              </Text>
            </>
          )}
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card withBorder h='100%'>
          <Title order={5}>Sessioni anomale nel gruppo</Title>
          {isLoading ? (
            <KpiValueSkeleton />
          ) : (
            <>
              <Text size='xl' fw={700}>
                {summary.outlierCount} sessioni
              </Text>
              <Text c='dimmed' size='sm' mt='xs'>
                Sessioni che si discostano in modo marcato dall&apos;andamento
                medio del gruppo su HR e LF/HF nel tratto completo.
              </Text>
              <Text c='dimmed' size='sm' mt='xs'>
                {summary.outlierSessionIds.length > 0
                  ? `Sessioni coinvolte: ${summary.outlierSessionIds.join(', ')}`
                  : 'Nessuna sessione anomala rilevata in questo workspace.'}
              </Text>
            </>
          )}
        </Card>
      </Grid.Col>
    </Grid>
  );
}
